import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { useAppStore, setAppStore, getAppStore, type IAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { imageAccept, videoAccept } from '@/consts/configs';
import useAuth from '@/hooks/useAuth';
import useFileUpload from '@/hooks/useFileUpload';
import useGenerate from '@/hooks/useGenerate';
import { useMediaHandlers } from '@/hooks/useMediaHandlers';
import useGenerateAction from '@/hooks/useGenerateAction';
import resolvePresetBySlug from '@/requests/library/resolvePresetBySlug';
import buildInputsFromPreset from '@/utils/preset/buildInputsFromPreset';
import { generateCanvasImage } from '@/utils/canvas/canvasUtils';
import uploadAndUpdateInput from '@/utils/canvas/uploadAndUpdateInput';
import isFormValid from '@/utils/isFormValid';
import revokeBlobUrl from '@/utils/file/revokeBlobUrl';
import { getInvalidTitle } from '@/utils/upload/getUploadErrorMessages';
import isNativeMobile from '@/utils/platform/isNativeMobile';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import sendUploadClickAnalytics from '@/utils/analytics/sendUploadClickAnalytics';
import { AiGenScreenAction, ClickItem, ScreenType } from '@/types/analytics';
import { InputType } from '@/types';

import type { IInput, IMediaInput } from '@/types';

const selector = (store: IAppStore) => ({
  inputs: store.inputs,
  selectedPreset: store.selectedPreset,
  pendingUploads: store.pendingUploads,
  showToast: store.showToast,
  updateInput: store.updateInput,
  addPendingUpload: store.addPendingUpload,
});

const getMediaInputs = (inputs: IInput[]): IMediaInput[] =>
  inputs.filter((input): input is IMediaInput => input.type !== InputType.TEXT);

interface AwaitingUpload {
  inputId: string;
  slug: string;
}

const usePresetFeedActions = () => {
  const queryClient = useQueryClient();
  const { uploadMedia } = useGenerate();
  const mediaHandlers = useMediaHandlers({ uploadMedia });
  const { handleGenerate, isSubmitting } = useGenerateAction();
  const { isAuthorized } = useAuth();

  const {
    inputs,
    pendingUploads,
    selectedPreset,
    showToast,
    updateInput,
    addPendingUpload,
  } = useAppStore(selector);

  const isOnNativeMobile = isNativeMobile();
  // True while the host flattens the canvas after a Generate tap (before the
  // preview/upload starts) — keeps the button in a loading state, no dead gap.
  const [isPreparingCanvas, setIsPreparingCanvas] = useState(false);

  // The upload we opened the picker for. Carries the slide's slug so an
  // upload that lands after the user swiped away (or input ids colliding
  // across presets) can be recognized and discarded instead of generating
  // under the wrong slide.
  const awaitingUploadRef = useRef<AwaitingUpload | null>(null);
  // A pick that hit the auth gate. Mirrors the pendingDriveAction pattern:
  // consumed by the post-login resume effect below once the parked upload
  // has swapped the blob preview for its CDN url.
  const pendingAuthGenerateRef = useRef<AwaitingUpload | null>(null);
  // A sheet Generate that hit the auth gate. Stores the slide's slug so the
  // post-login resume below can re-fire generation once uploads have settled.
  // The sheet can carry many media inputs (or none), so the slug alone — not a
  // single {inputId, slug} — identifies the pending intent.
  const pendingSheetGenerateRef = useRef<string | null>(null);
  // Last-request-wins guard: tracks the most recently requested preset slug.
  // Nulled on unmount so a fetch resolving after the feed closes can't
  // overwrite the Create screen's store state.
  const activeSlugRef = useRef<string | null>(null);
  // Blob preview URL created for a logged-out direct-picker upload. On the happy
  // path uploadMedia (post-login) and handleMediaRemove (swipe/re-pick) already
  // revoke it; this ref lets the unmount cleanup revoke it too when the user
  // abandons the pick (dismisses auth / closes the feed) before either runs.
  const previewBlobUrlRef = useRef<string | null>(null);
  const [shouldGenerate, setShouldGenerate] = useState(false);
  const [isInputsSheetOpen, setIsInputsSheetOpen] = useState(false);

  const mediaInputs = useMemo(() => getMediaInputs(inputs), [inputs]);
  const hasTextInputs = inputs.some((input) => input.type === InputType.TEXT);
  // The only preset shape whose Recreate tap opens the picker directly:
  // exactly one media input and nothing to type. Every other shape fills
  // its inputs through the inputs sheet.
  const directPickerInput =
    mediaInputs.length === 1 && !hasTextInputs ? mediaInputs[0] : null;

  // The direct-picker upload has landed (src set) but the upload-complete effect
  // below hasn't handed off to `shouldGenerate`/`isSubmitting` yet — the button
  // must stay busy across this beat so its label doesn't flash back. Reading the
  // ref in render mirrors `isActivePresetReady` below; it re-evaluates on every
  // state-driven render, and the effect nulls the ref the moment it arms
  // `shouldGenerate`, so this can't outlive the handoff.
  const isAwaitingDirectGenerate =
    !!directPickerInput?.src &&
    awaitingUploadRef.current?.inputId === directPickerInput.id;

  // Busy for the whole tap → upload → auto-generate handoff, with NO gaps, so
  // the button stays in its loading state (solid pill + spinner) continuously
  // instead of flashing the "Try Style" label back between the upload finishing
  // and generation starting. Chained, gap-free coverage:
  //   isLoading            picker open + CDN upload
  //   isAwaitingDirectGen  src landed, awaiting the arm effect
  //   shouldGenerate       armed, awaiting handleGenerate
  //   isSubmitting         generation dispatched
  // Still excludes background generation *polling* (isSubmitting drops once
  // generate() resolves) so a new generation can start while a previous one
  // polls. The button isn't disabled on this — it stays solid over the media;
  // handleUploadTap early-returns while it's true to drop an accidental
  // rapid double-tap.
  const isUploadBusy =
    !!directPickerInput?.isLoading ||
    isAwaitingDirectGenerate ||
    shouldGenerate ||
    isSubmitting ||
    isPreparingCanvas;

  // True once the active slide's own preset has loaded — i.e. `inputs` /
  // `directPickerInput` describe THIS slide, not the one we scrolled from.
  // Lets the Recreate button stay enabled during the background preset fetch:
  // a tap before this is ready opens the sheet (in a loading state) instead of
  // acting on the previous slide's inputs.
  const isActivePresetReady = selectedPreset?.slug === activeSlugRef.current;

  // The app-standard web file-picker machinery (same stack UploadArea uses):
  // owns the hidden input's ref/change handler, validates the file type, and
  // manages the blob-preview lifecycle. The feed has no dropzone UI, so
  // invalid files surface through the global toast.
  const accept =
    directPickerInput?.type === InputType.VIDEO ? videoAccept : imageAccept;
  const filePicker = useFileUpload({
    accept,
    value: directPickerInput?.src,
    hasUploadError: directPickerInput?.hasUploadError,
    onUpload: (file) => {
      const awaiting = awaitingUploadRef.current;
      if (!awaiting || activeSlugRef.current !== awaiting.slug) return false;
      // Mirror the native direct-picker: for a logged-out user, a blob preview in
      // the store `src` is what arms the auto-generate → registration-modal →
      // post-login resume chain (it gates on directPickerInput.src).
      // handleMediaUpload parks the file for the post-login CDN upload.
      if (!isAuthorized) {
        // Revoke any prior abandoned preview, then track the new blob so unmount
        // cleanup can free it if this pick is abandoned before login/removal.
        revokeBlobUrl(previewBlobUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        previewBlobUrlRef.current = previewUrl;
        updateInput({
          id: awaiting.inputId,
          value: {
            src: previewUrl,
            isLoading: false,
            hasUploadError: false,
          },
        });
      }
      return mediaHandlers.handleMediaUpload(awaiting.inputId, file);
    },
    showInvalidFeedback: () =>
      showToast(getInvalidTitle({ accept }), ToastType.Error),
  });

  const loadActivePreset = useCallback(
    async (slug: string) => {
      activeSlugRef.current = slug;
      setIsInputsSheetOpen(false);
      setAppStore({ isPresetLoading: true });
      try {
        const preset = await resolvePresetBySlug(slug, queryClient);
        const built = buildInputsFromPreset(preset);

        const isSamePreset = getAppStore().selectedPreset?.slug === slug;
        const inputsForFeed = isSamePreset
          ? built
          : built.map((input) =>
              input.type === InputType.TEXT ? input : { ...input, src: '' }
            );
        if (activeSlugRef.current !== slug) return;
        setAppStore({
          selectedPreset: { ...preset, slug },
          inputs: inputsForFeed,
          isPresetLoading: false,
        });
      } catch (error) {
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
        if (activeSlugRef.current === slug) {
          setAppStore({ isPresetLoading: false });
        }
      }
    },
    [queryClient]
  );

  const openPickerForInput = useCallback(
    (input: IMediaInput) => {
      const slug = activeSlugRef.current;
      if (!slug) return;
      awaitingUploadRef.current = { inputId: input.id, slug };
      if (isOnNativeMobile) {
        mediaHandlers.handleSelectFromNativePicker(input.id, input.type);
      } else {
        // Web: the click stays in the gesture — openFilePicker is synchronous.
        filePicker.openFilePicker();
      }
    },
    [mediaHandlers, isOnNativeMobile, filePicker]
  );

  const handleUploadTap = useCallback(() => {
    if (isUploadBusy) return;

    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CLICK,
      screen_type: ScreenType.PRESET_FEED,
      item: ClickItem.Effect,
      settings: {
        category: getAppStore().activeTabSlug ?? '',
        id: activeSlugRef.current ?? '',
      },
    });
    // Preset for this slide is still loading — `directPickerInput` may describe
    // the previous slide, so never open the picker directly here. The sheet
    // needs no tap-gesture and shows a loading state until the preset lands.
    if (!isActivePresetReady) {
      setIsInputsSheetOpen(true);
      return;
    }
    if (directPickerInput) {
      // Direct picker has no DropZone — fire upload_click here (the sheet's
      // slots fire their own via DropZone).
      sendUploadClickAnalytics();

      // Already filled → let the user pick a different image (change flow).
      if (directPickerInput.src) {
        mediaHandlers.handleMediaRemove(directPickerInput.id);
        openPickerForInput(directPickerInput);
        return;
      }

      // Empty input: if the host canvas has content, use it and generate —
      // skip the chooser. `generateCanvasImage` returns null fast when the
      // canvas is empty, in which case we fall through to the picker. Setting
      // the input src arms the existing shouldGenerate flow (incl. the
      // logged-out → login resume), so no separate generate path is needed.
      const slug = activeSlugRef.current;
      setIsPreparingCanvas(true);
      generateCanvasImage()
        .then(async (canvasImage) => {
          if (!canvasImage || !slug || slug !== activeSlugRef.current) {
            openPickerForInput(directPickerInput);
            return;
          }
          awaitingUploadRef.current = { inputId: directPickerInput.id, slug };
          await uploadAndUpdateInput({
            inputId: directPickerInput.id,
            image: canvasImage,
            updateInput,
            isAuthorized,
            addPendingUpload,
            // User tapped Recreate → this is a user-initiated upload; pair the
            // UPLOAD event with the upload_click already sent above.
            trackUpload: true,
          });
        })
        .catch((error) => {
          errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
          openPickerForInput(directPickerInput);
        })
        .finally(() => setIsPreparingCanvas(false));
      return;
    }
    setIsInputsSheetOpen(true);
  }, [
    directPickerInput,
    updateInput,
    isAuthorized,
    addPendingUpload,
    isActivePresetReady,
    openPickerForInput,
    mediaHandlers,
    isUploadBusy,
  ]);

  const closeSheet = useCallback(() => {
    setIsInputsSheetOpen(false);
  }, []);

  const handleSheetGenerate = useCallback(() => {
    if (isSubmitting) return;
    if (isAuthorized) {
      // Authorized: generate now and dismiss the sheet.
      setIsInputsSheetOpen(false);
    } else {
      // Logged out: keep the sheet open behind the registration modal and arm
      // the post-login resume so a successful login generates without the user
      // re-tapping Recreate. The dispatch below opens the registration modal.
      pendingSheetGenerateRef.current = activeSlugRef.current;
    }
    handleGenerate().catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  }, [handleGenerate, isSubmitting, isAuthorized]);

  useEffect(() => {
    if (!directPickerInput?.src) return;
    const awaiting = awaitingUploadRef.current;
    if (!awaiting || awaiting.inputId !== directPickerInput.id) return;

    if (isAuthorized && !isFormValid(inputs)) return;
    awaitingUploadRef.current = null;
    // The upload belongs to the slide it was picked on. If the user swiped
    // away while it uploaded, discard it — generating another slide's preset
    // with this photo (or leaving it stuck in that slide's slot) is worse.
    if (awaiting.slug !== activeSlugRef.current) {
      mediaHandlers.handleMediaRemove(directPickerInput.id);
      return;
    }
    // Logged out, the file is parked in pendingUploads with a blob preview.
    // Still dispatch (the auth gate opens the registration modal) but arm the
    // post-login resume so a successful login picks the flow back up.
    if (!isAuthorized) pendingAuthGenerateRef.current = awaiting;
    setShouldGenerate(true);
  }, [directPickerInput, mediaHandlers, isAuthorized, inputs]);

  // Post-login resume for the direct-picker flow. Waits for usePendingUploads
  // to finish the deferred CDN upload — while the input still sits in
  // pendingUploads (or is mid-upload) the src is a blob: url that must never
  // reach the backend.
  useEffect(() => {
    if (!isAuthorized) return;
    const pending = pendingAuthGenerateRef.current;
    if (!pending) return;
    // Swiped to another slide during login — the photo no longer belongs to
    // the active preset.
    if (pending.slug !== activeSlugRef.current) {
      pendingAuthGenerateRef.current = null;
      return;
    }
    if (!directPickerInput || directPickerInput.id !== pending.inputId) return;
    if (!directPickerInput.src || directPickerInput.isLoading) return;
    if (pendingUploads.some((upload) => upload.id === pending.inputId)) return;
    pendingAuthGenerateRef.current = null;
    setShouldGenerate(true);
  }, [isAuthorized, directPickerInput, pendingUploads]);

  // Post-login resume for the inputs-sheet flow (every preset shape that isn't
  // the single-media direct picker: prompt-only, image + prompt, multi-image,
  // video). The sheet lets a logged-out user tap Generate on a partially filled
  // form (the showSignIn bypass), so gate the resume on the strict isFormValid:
  // fire only once usePendingUploads has swapped every blob preview for its CDN
  // url and all inputs are valid. Prompt-only presets have no media to wait on.
  useEffect(() => {
    if (!isAuthorized) return;
    const pendingSlug = pendingSheetGenerateRef.current;
    if (!pendingSlug) return;
    // Swiped to another slide during login — the intent no longer applies.
    if (pendingSlug !== activeSlugRef.current) {
      pendingSheetGenerateRef.current = null;
      return;
    }
    if (pendingUploads.length > 0) return;
    if (!isFormValid(inputs)) return;
    pendingSheetGenerateRef.current = null;
    setShouldGenerate(true);
  }, [isAuthorized, pendingUploads, inputs]);

  // Invalidate in-flight work when the feed closes: a preset fetch or upload
  // resolving after unmount must not touch the store the Create screen uses.
  useEffect(
    () => () => {
      activeSlugRef.current = null;
      awaitingUploadRef.current = null;
      pendingAuthGenerateRef.current = null;
      pendingSheetGenerateRef.current = null;
      revokeBlobUrl(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!shouldGenerate) return;
    setShouldGenerate(false);
    handleGenerate().catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  }, [shouldGenerate, handleGenerate]);

  return {
    loadActivePreset,
    handleUploadTap,
    isUploadLoading: isUploadBusy,
    isInputsSheetOpen,
    closeSheet,
    handleSheetGenerate,
    isGenerating: isSubmitting,
    filePickerInputProps: {
      inputRef: filePicker.inputRef,
      accept,
      onChange: filePicker.handleChange,
    },
  };
};

export default usePresetFeedActions;
